import { useState, useMemo } from 'react';
import EditAppointmentModal from './EditAppointmentModal';

/**
 * Vista de calendario semanal.
 * Muestra slots de disponibilidad del doctor (bloques de fondo) +
 * citas existentes (eventos clicables) agrupados por día.
 */
const HOURS = Array.from({ length: 17 }, (_, i) => i + 7); // 07:00 – 23:00, cubre franjas nocturnas
const DAY_NAMES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

// Mapa día-de-semana (getDay) → clave de availability
const DOW_TO_KEY = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

const STATUS_COLORS = {
  scheduled:     '#3b82f6',
  confirmed:     '#14b8a6',
  'in-progress': '#f59e0b',
  completed:     '#22c55e',
  cancelled:     '#ef4444',
  'no-show':     '#6b7280',
};

const startOfWeek = (d) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() - dt.getDay());
  dt.setHours(0,0,0,0);
  return dt;
};

// Día local 'YYYY-MM-DD'. Con toISOString (UTC) una cita de las 19:00 o más
// tarde en Bogotá caía en la columna del día siguiente.
const localDayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmtDay = (d) => d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit' });

const hourOf = (hhmm) => parseInt(hhmm?.split(':')[0] ?? '0', 10);

/**
 * Dada la disponibilidad del doctor y un día de la semana (Date),
 * retorna si la hora `h` está dentro de alguna de sus franjas (la segunda,
 * start2/end2, es opcional).
 */
const isAvailableHour = (availability, date, h) => {
  if (!availability) return false;
  const key  = DOW_TO_KEY[date.getDay()];
  const slot = availability[key];
  if (!slot || !slot.available) return false;
  const ranges = [[slot.start, slot.end]];
  if (slot.start2 && slot.end2) ranges.push([slot.start2, slot.end2]);
  return ranges.some(([start, end]) => h >= hourOf(start) && h < hourOf(end));
};

export default function AppointmentCalendar({ appointments, availability, onEdit, onCancel }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [editTarget, setEdit]     = useState(null);

  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    }), [weekStart]
  );

  const prevWeek = () => setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate()-7); return n; });
  const nextWeek = () => setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate()+7); return n; });

  const byDay = useMemo(() => {
    const map = {};
    appointments.forEach(a => {
      const key = localDayKey(new Date(a.scheduledDate));
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [appointments]);

  return (
    <div className="appointment-calendar">
      <div className="cal-nav">
        <button onClick={prevWeek}>&lt; Semana anterior</button>
        <span className="week-label">
          {fmtDay(weekDays[0])} — {fmtDay(weekDays[6])}
        </span>
        <button onClick={nextWeek}>Semana siguiente &gt;</button>
      </div>

      {/* Leyenda */}
      <div className="cal-legend">
        <span className="legend-available">Disponible</span>
        <span className="legend-unavailable">Sin disponibilidad</span>
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <span key={status} className="legend-item" style={{ '--dot-color': color }}>
            {status}
          </span>
        ))}
      </div>

      <div className="cal-grid">
        {/* Header row */}
        <div className="cal-time-col" />
        {weekDays.map((d, i) => (
          <div key={i} className="cal-day-header">
            <span>{DAY_NAMES[d.getDay()]}</span>
            <span>{fmtDay(d)}</span>
          </div>
        ))}

        {/* Hour rows */}
        {HOURS.map(h => (
          <>
            <div key={`h${h}`} className="cal-time-label">{String(h).padStart(2,'0')}:00</div>
            {weekDays.map((d, di) => {
              const key   = localDayKey(d);
              const avail = isAvailableHour(availability, d, h);
              const slots = (byDay[key] || []).filter(a => {
                const hour = new Date(a.scheduledDate).getHours();
                return hour === h;
              });
              return (
                <div
                  key={`${h}-${di}`}
                  className={`cal-cell ${avail ? 'cal-cell--available' : 'cal-cell--unavailable'}`}
                >
                  {slots.map(a => (
                    <div
                      key={a._id}
                      className="cal-event"
                      style={{ backgroundColor: STATUS_COLORS[a.status] ?? '#3b82f6' }}
                      title={`${a.patient?.user?.profile?.firstName} — ${a.reason}`}
                      onClick={() => setEdit(a)}
                    >
                      <span>{new Date(a.scheduledDate).toLocaleTimeString('es-CO', { timeZone: 'America/Bogota', hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="cal-patient">{a.patient?.user?.profile?.firstName}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </>
        ))}
      </div>

      {editTarget && (
        <EditAppointmentModal
          appointment={editTarget}
          onClose={() => setEdit(null)}
          onSave={async (updates) => { await onEdit(editTarget._id, updates); setEdit(null); }}
          onCancel={async () => { await onCancel(editTarget._id); setEdit(null); }}
        />
      )}
    </div>
  );
}
